<script>
/* COMPOSANTS */
import PrestatairePageMissions from './tabs/PrestatairePageMissions.vue';
import GaTripleView from '@components/v2/layout/GaTripleView.vue';
import AkAvatar from '@components/general/AkAvatar.vue';
import AkButtonAction from '@components/v2/general/AkButtonAction.vue';
import PrestataireUpdateSidebar from '@views/prestataires/PrestataireUpdateSidebar.vue';
import AkButtonActionValidation from '@components/v2/general/AkButtonActionValidation.vue';

/* MIXINS */
import randomRef from '@mixins/randomRef';
import backMixin from '@mixins/backMixin';
import messageMixin from '@mixins/messageMixin';
import companyMixin from '@mixins/companyMixin';
import permissionsMixin from '@mixins/permissionsMixin';
import civilitiesConst from '@mixins/const/civilitiesConst';
import UtilsMixin from '@mixins/utilsMixin';
import LoaderMixin from '@mixins/loaderMixin';

/* SERVICES */
import prestataireService from '@services/prestataireService';
import ficService from '@services/ficService';
import AkFicListColRight from '@components/v2/general/AkFicListColRight.vue';
import useVuelidate from '@vuelidate/core';
import {helpers, maxLength, required} from '@vuelidate/validators';
import validatorMixins from '@mixins/validatorMixins';
import DropdownMenu from 'v-dropdown-menu';
import AkFormSubmitted from '@components/general/AkFormSubmitted.vue';
import AkInputText from '@components/v2/input/AkInputText.vue';
import AkDialog from '@components/general/AkDialog.vue';
import AkButtonActionV2 from '@components/v2/general/AkButtonActionV2.vue';

export default {
  components: {
    AkButtonActionV2,
    AkDialog,
    AkInputText,
    AkFormSubmitted,
    AkButtonActionValidation,
    AkFicListColRight,
    PrestataireUpdateSidebar,
    AkButtonAction,
    GaTripleView,
    PrestatairePageMissions,
    AkAvatar,
    DropdownMenu,
  },
  mixins: [
    randomRef,
    backMixin,
    messageMixin,
    companyMixin,
    permissionsMixin,
    civilitiesConst,
    UtilsMixin,
    LoaderMixin,
    validatorMixins,
  ],
  head() {
    return {
      title: this.title,
    };
  },
  setup() {
    return {v$: useVuelidate()};
  },
  validations() {
    return {
      email: {
        required,
        maxLengthValue: maxLength(100),
        email: helpers.withMessage(
          "Le format de l'adresse e-mail est invalide. Veuillez vérifier et réessayer (ex. : nom@domaine.com)",
          this.emailValidator,
        ),
      },
    };
  },
  data() {
    return {
      prestataire: {
        id: null,
        companyId: null,
        contact: {
          civility: '',
          firstName: '',
          lastName: '',
          birthDate: new Date(),
          email: '',
          phone: '',
        },
        files: [],
        newFiles: [],
      },
      email: null,
      previousEmail: null,
      submitted: false,
    };
  },
  mounted() {
    this.initGoBack();
    this.refresh();
  },
  computed: {
    title() {
      return this.prestataire.contact.firstName + ' ' + this.prestataire.contact.lastName;
    },
  },
  methods: {
    refresh() {
      this.showTotalLoaderGuestadom();
      prestataireService
        .get(this.$route.params.prestataireId)
        .then((data) => {
          this.prestataire = data;
        })
        .catch((e) => {
          this.addError(e.response.data.error);
        })
        .finally(() => {
          this.hideLoader();
        });
    },
    enablePrestataire() {
      prestataireService
        .enable(this.prestataire.id)
        .then(() => {
          this.prestataire.enabled = true;
          this.success(this.$t('prestataire.activated'));
        })
        .catch((e) => {
          this.addError(e.response.data.error);
        })
        .finally(() => {
          if (this.$refs.btnEnable) this.$refs.btnEnable.init();
        });
    },
    disablePrestataire() {
      prestataireService
        .disable(this.prestataire.id)
        .then(() => {
          this.prestataire.enabled = false;
          this.success(this.$t('prestataire.deactivated'));
        })
        .catch((e) => {
          this.addError(e.response.data.error);
        })
        .finally(() => {
          if (this.$refs.btnDisable) this.$refs.btnDisable.init();
        });
    },
    openUpdateSidebar() {
      if (this.$refs.prestataireUpdateSidebar) this.$refs.prestataireUpdateSidebar.show(this.prestataire.id);
    },
    reloadFiles() {
      ficService.fics({tableName: 'prestataire', tableId: this.prestataire.id}).then((data) => {
        this.prestataire.files = data;
      });
    },
    showEmailUpdateDialog() {
      this.previousEmail = this.prestataire.contact.email;
      this.submitted = false;
      this.email = null;
      this.$refs.updateEmailDialog.show();
    },
    updateEmail() {
      this.submitted = true;
      this.v$.email.$touch();
      if (this.v$.email.$error) {
        this.$refs.updateEmailDialog.init();
        return;
      }

      prestataireService
        .updateEmail(this.prestataire.id, this.email)
        .then(() => {
          this.prestataire.contact.email = this.email;

          this.addSuccess("L'adresse mail du prestataire a été modifiée avec succès");
          this.$refs.updateEmailDialog.cancel();
        })
        .catch((e) => {
          this.$refs.updateEmailDialog.init();
          if (e.response.data.error === 'USER_MAIL_ALREADY_EXISTS')
            this.$refs.updateEmailDialog.error(
              "L'adresse mail saisie est déjà enregistrée dans l'application. Merci de vérifier le champ E-mail.",
            );
          else this.$refs.updateEmailDialog.error('Une erreur est survenu, merci de réessayer.');
        });
    },
  },
};
</script>

<template>
  <GaTripleView>
    <template #action>
      <AkButtonActionValidation
        v-if="!prestataire.enabled"
        ref="btnEnable"
        label="Activer le prestataire"
        :messagePopup="$t('confirm_enable', [prestataire.display])"
        :titlePopup="$t('confirm_dialog')"
        success
        @click="enablePrestataire" />
      <AkButtonActionValidation
        v-else
        ref="btnDisable"
        label="Désactiver le prestataire"
        :messagePopup="$t('confirm_disable', [prestataire.display])"
        :titlePopup="$t('confirm_dialog')"
        danger
        @click="disablePrestataire" />

      <dropdown-menu :overlay="false" direction="right" class="accommodation">
        <template #trigger>
          <span class="btn-primary xs">Modifier</span>
        </template>
        <template #body>
          <ul>
            <li class="py-2">
              <AkButtonActionV2
                label="Modifier le prestataire"
                class="text-grey-400 font-medium font-inter !bg-transparent !p-0 ext-base md:hover:text-grey-900"
                @click="openUpdateSidebar" />
            </li>
            <li class="py-2">
              <AkButtonActionV2
                label="Modifier l'e-mail"
                class="text-grey-400 font-medium font-inter !bg-transparent !p-0 ext-base md:hover:text-grey-900"
                @click="showEmailUpdateDialog" />
            </li>
          </ul>
        </template>
      </dropdown-menu>
    </template>
    <template #left>
      <div class="flex flex-col gap-4 md:gap-2">
        <div class="w-[160px] self-center">
          <AkAvatar
            v-if="prestataire.userId"
            ref="avatar"
            :first-name="prestataire.contact.firstName"
            :last-name="prestataire.contact.lastName"
            :user-id="prestataire.userId" />
        </div>

        <div class="flex items-center gap-2 mt-3">
          <i
            class="ga-icon ga-chevron_down text-grey-900 text-2xl md:hover:cursor-pointer"
            @click="toggleElement(this.$refs, 'data', $event)" />
          <span class="font-inter text-grey-900 font-semibold text-lg">Informations</span>
        </div>
        <div ref="data">
          <div class="grid grid-cols-3 mt-4 gap-4 md:gap-2">
            <div class="flex flex-col gap-1">
              <span class="font-inter font-semibold text-grey-900 text-base">{{ $t('prestataire.civility') }}</span>
              <span class="font-inter font-medium text-grey-400 text-base">{{
                displayForCivilityConst(prestataire.contact.civility)
              }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="font-inter font-semibold text-grey-900 text-base">{{ $t('prestataire.lastName') }}</span>
              <span class="font-inter font-medium text-grey-400 text-base">{{ prestataire.contact.lastName }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="font-inter font-semibold text-grey-900 text-base">{{ $t('prestataire.firstName') }}</span>
              <span class="font-inter font-medium text-grey-400 text-base">{{ prestataire.contact.firstName }}</span>
            </div>
          </div>
          <div class="grid grid-cols-2 mt-4 gap-4 md:gap-2">
            <div class="flex flex-col gap-1">
              <span class="font-inter font-semibold text-grey-900 text-base">{{ $t('prestataire.birthDate') }}</span>
              <span class="font-inter font-medium text-grey-400 text-base">{{
                formatDateIfNeeded(prestataire.contact.birthDate)
              }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="font-inter font-semibold text-grey-900 text-base">{{ $t('prestataire.company') }}</span>
              <span class="font-inter font-medium text-grey-400 text-base">{{
                displayForCompany(prestataire.companyId)
              }}</span>
            </div>
          </div>
        </div>

        <div class="flex items-center gap-2 mt-3">
          <i
            class="ga-icon ga-chevron_down text-grey-900 text-2xl md:hover:cursor-pointer"
            @click="toggleElement(this.$refs, 'coordinates', $event)" />
          <span class="font-inter text-grey-900 font-semibold text-lg">Coordonnées</span>
        </div>
        <div ref="coordinates">
          <div class="grid grid-cols-1 mt-4 gap-4 md:gap-2">
            <div class="flex flex-col gap-1">
              <span class="font-inter font-semibold text-grey-900 text-base">{{ $t('prestataire.email') }}</span>
              <span class="font-inter font-medium text-grey-400 text-base">{{ prestataire.contact.email }}</span>
            </div>
          </div>
          <div class="grid grid-cols-2 mt-4 gap-4 md:gap-2">
            <div class="flex flex-col gap-1">
              <span class="font-inter font-semibold text-grey-900 text-base">{{ $t('prestataire.phone') }}</span>
              <span class="font-inter font-medium text-grey-400 text-base">{{ prestataire.contact.phone }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="font-inter font-semibold text-grey-900 text-base">{{ $t('prestataire.homePhone') }}</span>
              <span class="font-inter font-medium text-grey-400 text-base">{{ prestataire.contact.homePhone }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #content>
      <PrestatairePageMissions :prestataire-id="this.$route.params.prestataireId" />
    </template>
    <template #right>
      <AkFicListColRight
        v-if="prestataire.id"
        v-model="prestataire.files"
        :table-id="prestataire.id"
        can-delete
        can-upload
        fic-type="PRESTATAIRE_REGISTRATION"
        table-name="prestataire"
        title="Pièces jointes" />
    </template>

    <template #extra>
      <PrestataireUpdateSidebar ref="prestataireUpdateSidebar" @update="refresh" @updateFile="reloadFiles" />
    </template>
  </GaTripleView>

  <AkDialog title="Modifier l'e-mail" ref="updateEmailDialog" :validate-label="$t('modify')" @validate="updateEmail">
    <AkFormSubmitted :submitted="submitted" class="flex flex-col gap-2">
      <AkInputText v-model="this.previousEmail" label="Ancienne adresse mail" disabled />
      <AkInputText v-model="this.email" :validator="v$.email" label="Nouvelle adresse mail" autofocus />
    </AkFormSubmitted>
  </AkDialog>
</template>
